import { useState } from "react";
import { api, setAuthToken } from "../api/client";
import { Button, Field, Icon, Input } from "../components/ui";

// ── Satu pintu masuk, peran ditentukan SESUDAH login ────────────────────────
//
// Dulu halaman ini punya tab peran (Trainer / Admin / Mgmt / Leader / Staff) yang
// harus dipilih SEBELUM login. Pemeriksaannya pun baru terjadi setelah password
// terbukti benar — jadi tab itu tak memberi keamanan apa pun, hanya menebak di muka
// informasi yang sudah dipegang server. Menebak salah menghasilkan penolakan untuk
// orang yang kredensialnya BENAR.
//
// Sekarang: login dulu, lalu perannya dibaca dari jawaban server.
//   • satu peran  → langsung masuk (seluruh akun produksi saat ini);
//   • multi peran → kartu pilihan, dan BELUM masuk sampai salah satunya ditekan.
//
// Diindeks per kunci peran kanonik (sama dengan users.roles), bukan per label tab,
// supaya jawaban server bisa dicari langsung tanpa penerjemahan.
const ROLE_CONFIG = {
  trainer: {
    label: "Trainer",
    subtitle: "Absensi kelas, rekap, dan slip gaji",
    icon: "auto_awesome"
  },
  admin: {
    label: "Admin",
    subtitle: "Kelola cabang, akun, dan payroll",
    icon: "admin_panel_settings"
  },
  team_leader: {
    label: "Team Leader",
    subtitle: "Jadwal, absensi, dan payroll cabangmu",
    icon: "supervisor_account"
  },
  staff: {
    label: "Staff",
    subtitle: "Lihat dan unduh slip gajimu",
    icon: "badge"
  },
  management: {
    label: "Management",
    subtitle: "Pantau absensi lintas cabang",
    icon: "monitoring"
  }
};

// Urutan kartu: yang paling sering dipakai sehari-hari di atas.
const URUTAN = ["trainer", "team_leader", "admin", "staff", "management"];

// Peran untuk akun LAMA yang kolom `roles`-nya masih kosong. Aturannya disamakan
// dengan turunan di Shell dan di parseRoles (server) — tiga tempat yang harus
// sepakat, kalau tidak orang yang sama bisa masuk sebagai peran berbeda tergantung
// pintu mana yang menilainya.
//
// Tanpa jejak apa pun → trainer. Arah gagalnya disengaja: portal paling sempit
// lebih baik daripada menolak orang yang passwordnya benar.
function peranDariPosition(position) {
  const p = String(position || "").trim().toLowerCase();
  if (/admin/.test(p)) return "admin";
  if (/management/.test(p)) return "management";
  if (/team\s*leader/.test(p) || p === "leader") return "team_leader";
  return "trainer";
}

function peranAkun(user) {
  const dikenal = (Array.isArray(user?.roles) ? user.roles : [])
    .map((r) => String(r || "").trim().toLowerCase())
    .filter((r) => ROLE_CONFIG[r]);
  const unik = [...new Set(dikenal)];
  if (unik.length) return URUTAN.filter((r) => unik.includes(r));
  return [peranDariPosition(user?.position)];
}

export default function Login({ onLogin, setToast, theme, toggleTheme }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Diisi hanya bila akunnya punya LEBIH dari satu peran: jawaban login disimpan
  // di sini sampai pengguna memilih kartunya.
  const [pilihan, setPilihan] = useState(null); // { res, roles: [...] }

  function masuk(res, roleKey) {
    // Token disimpan saat pengguna benar-benar MASUK, bukan saat password lolos.
    // Kalau ia berhenti di layar kartu, tak ada sesi setengah jadi yang tertinggal.
    setAuthToken(res.token || null);

    onLogin({
      ...res.user,
      selectedRole: roleKey,
      activeRole: roleKey,
      keepSignedIn
    });

    setToast({
      type: "success",
      message: `Masuk sebagai ${ROLE_CONFIG[roleKey]?.label || roleKey}`
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (busy) return;

    setBusy(true);
    setError("");

    try {
      const res = await api.login(email.trim(), password, keepSignedIn);

      if (!res?.success) {
        setError(res?.message || "Invalid Email or Password.");
        return;
      }

      const roles = peranAkun(res.user);
      if (roles.length === 1) {
        masuk(res, roles[0]);
        return;
      }
      setPilihan({ res, roles });
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="relative min-h-screen overflow-hidden px-4 py-8">
      <div className="pointer-events-none absolute -left-20 -top-20 h-96 w-96 rounded-full bg-primary/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-primary/20 blur-[120px]" />

      <button
        type="button"
        onClick={toggleTheme}
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        className="absolute right-4 top-4 z-20 rounded-xl border border-slate-200 bg-white/80 p-2.5 text-slate-500 shadow-lg backdrop-blur hover:text-primary dark:border-slate-700 dark:bg-slate-900/70"
      >
        <Icon name={theme === "dark" ? "light_mode" : "dark_mode"} />
      </button>

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl flex-col items-center justify-center">
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src={process.env.PUBLIC_URL + "/logo-kip.jpeg"}
            alt="Kampung Inggris Pontianak"
            className="h-24 w-auto rounded-2xl bg-white p-2 shadow-xl"
          />
        </div>

        <div className="neon-border w-full max-w-md rounded-2xl border bg-white/95 p-8 shadow-2xl backdrop-blur dark:bg-slate-900/70">
          {pilihan ? (
            <PilihPeran
              nama={pilihan.res.user?.nickname || pilihan.res.user?.name || ""}
              roles={pilihan.roles}
              onPilih={(r) => masuk(pilihan.res, r)}
              onKembali={() => {
                setPilihan(null);
                setPassword("");
              }}
            />
          ) : (
            <>
              <div className="mb-7 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                  <Icon name="login" className="text-3xl" />
                </div>

                <h2 className="text-3xl font-black">Welcome Back</h2>
                <p className="mt-2 text-sm text-slate-500">Sign in to KIP Portal</p>

                {error && (
                  <p className="mt-4 rounded-lg bg-red-500/10 px-3 py-2 text-sm font-bold text-red-500">
                    {error}
                  </p>
                )}
              </div>

              <form className="space-y-5" onSubmit={submit}>
                <Field label="Email Address">
                  <Input
                    type="email"
                    aria-label="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@kip.com"
                    autoComplete="email"
                    required
                  />
                </Field>

                <Field label="Password">
                  <div className="relative">
                    <Input
                      type={showPass ? "text" : "password"}
                      aria-label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      autoComplete="current-password"
                      required
                      className="pr-12"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      aria-label={showPass ? "Sembunyikan password" : "Tampilkan password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-primary"
                    >
                      <Icon name={showPass ? "visibility_off" : "visibility"} />
                    </button>
                  </div>
                </Field>

                <div className="flex items-center justify-between text-xs">
                  <label className="flex items-center gap-2 text-slate-500">
                    <input
                      type="checkbox"
                      checked={keepSignedIn}
                      onChange={(e) => setKeepSignedIn(e.target.checked)}
                      className="rounded text-primary focus:ring-primary"
                    />
                    Keep me signed in
                  </label>

                  <button
                    type="button"
                    className="font-bold text-primary"
                    onClick={() =>
                      setToast({
                        type: "success",
                        message: "Please contact Admin to reset your password."
                      })
                    }
                  >
                    Forgot password?
                  </button>
                </div>

                <Button className="w-full py-3" disabled={busy}>
                  {busy ? (
                    <>
                      <Icon name="progress_activity" className="animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Sign In
                      <Icon name="arrow_forward" />
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="mt-8 text-xs text-slate-500">
          © 2026 KIP Intelligence Systems. All rights reserved.
        </p>
      </div>
    </section>
  );
}

// ── Kartu pilihan peran — hanya untuk akun multi-peran ──────────────────────
function PilihPeran({ nama, roles, onPilih, onKembali }) {
  return (
    <div className="anim-fade">
      <div className="mb-6 text-center">
        <h2 className="text-2xl font-black">Pilih peran</h2>
        <p className="mt-2 text-sm text-slate-500">
          {nama ? `Halo, ${nama}. ` : ""}Akunmu punya lebih dari satu peran — masuk sebagai apa?
        </p>
      </div>

      <div className="space-y-3">
        {roles.map((r, i) => {
          const k = ROLE_CONFIG[r];
          return (
            <button
              key={r}
              type="button"
              onClick={() => onPilih(r)}
              className={`anim-rise anim-d${Math.min(4, i)} lift flex w-full items-center gap-4 rounded-xl border border-slate-200 p-4 text-left transition hover:border-primary/50 hover:bg-primary/5 dark:border-slate-700`}
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Icon name={k.icon} className="text-2xl" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-black">{k.label}</span>
                <span className="block text-xs text-slate-500">{k.subtitle}</span>
              </span>
              <Icon name="arrow_forward" className="text-slate-400" />
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onKembali}
        className="mt-6 w-full text-center text-xs font-bold text-slate-500 hover:text-primary"
      >
        Bukan akunmu? Kembali
      </button>
    </div>
  );
}
